import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  ForwardedRef,
} from 'react';
import { Table, TableProps, TableColumnType, PaginationProps } from 'antd';
import { SortOrder } from 'antd/lib/table/interface';
import { castArray, get, isEmpty, merge } from 'lodash';
import { withDefault, NumberParam, StringParam, useQueryParams, JsonParam } from 'use-query-params';
import dayjs from 'dayjs';
import { UseAPI, Variables } from 'api';
import Filters, { FiltersMap, FiltersProps } from '../Filters';
import ResizeableTitle, { ResizeableTitleProps } from './ResizeableTitle';
import './styles.scoped.css';

const DEFAULT_PAGE_SIZE = 25;

type RecordType = PlainObject<string | number>;

export interface DataTableRenderFields {
  data: any[];
  loading?: boolean;
  pagination?: PaginationProps;
}

interface DataTableProps {
  api: UseAPI;
  rowKey?: string;
  dataKey?: string;
  columns: TableColumnType<RecordType>[];
  transformData?: <T>(data: T) => T;
  onData?: (data: any) => void;
  onError?: (error: any) => void;
  apiParams?: Variables & { sortBy?: string; sortOrder?: SortOrder };
  apiHeaders?: Variables;
  apiInput?: Variables;
  filtersMap?: FiltersMap;
  filtersProps?: Pick<FiltersProps, 'updateQuery' | 'cardProps' | 'extraBtns'>;
  autoSubmit?: boolean;
  onFiltersSubmit?: (params: any, headers: any) => void;
  onFiltersReset?: () => void;
  tableProps?: TableProps<RecordType>;
  render?: (fields: DataTableRenderFields) => JSX.Element;
  useQuery?: boolean;
  loading?: boolean;
  showFilters?: boolean;
}

const components = {
  header: {
    cell: ResizeableTitle,
  },
};

export const queryParams = {
  page: NumberParam,
  pageSize: withDefault(NumberParam, DEFAULT_PAGE_SIZE, true),
  sortBy: StringParam,
  sortOrder: StringParam,
  // fromDate: StringParam,
  // toDate: StringParam,
  filters: JsonParam,
};

// export type RefObject = { reload: () => void };
export type RefObject = {
  reload: () => void;
  getCurrentParams: () => any;
};

function DataTable(props: DataTableProps, ref: ForwardedRef<RefObject>) {
  const {
    api: useApi,
    rowKey = 'id',
    dataKey = 'items',
    columns: propColumns,
    tableProps,
    onData,
    onError,
    transformData,
    apiParams: apiParamsProps,
    apiHeaders: apiHeadersProps,
    apiInput: apiInputProps,
    useQuery = true,
    loading: propLoading = false,
    filtersMap,
    filtersProps,
    autoSubmit = true,
    onFiltersSubmit,
    onFiltersReset,
    render,
    showFilters = true,
  } = props;
  const onDataRef = useRef(onData);
  onDataRef.current = onData;

  // Use refs to track latest props without causing re-renders
  const apiParamsPropsRef = useRef(apiParamsProps);
  const apiHeadersPropsRef = useRef(apiHeadersProps);
  apiParamsPropsRef.current = apiParamsProps;
  apiHeadersPropsRef.current = apiHeadersProps;

  const [query, setQuery] = useQueryParams(queryParams);
  const [tableData, setTableData] = useState({ data: [], total: 0 });
  const [tableVariables, setTableVariables] = useState(() => {
    return {
      page: query.page,
      limit: query.pageSize,
      sortBy: query.sortBy ?? apiParamsProps?.sortBy,
      sortOrder: query.sortOrder ?? apiParamsProps?.sortOrder,
      // fromDate: query.fromDate ?? apiParamsProps?.fromDate,
      // toDate: query.toDate ?? apiParamsProps?.toDate,
    };
  });
  const [apiParams, setApiParams] = useState(apiParamsProps || {});
  const [apiHeaders, setApiHeaders] = useState(apiHeadersProps || {});
  const pageSizeChange = useRef(false);
  const initialMount = useRef(true);
  const isFetchingRef = useRef(false);
  const hasFilters = !isEmpty(filtersMap) && showFilters;
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});

  const transformedColumns: any = useMemo(() => {
    return propColumns.map(
      (
        { dataIndex, key = castArray(dataIndex).join('_'), width = 120, align = 'center', ...rest },
        index,
      ) => ({
        dataIndex,
        key,
        align,
        // defaultSortOrder:
        sortOrder:
          tableVariables.sortBy === castArray(dataIndex).join('.')
            ? ((tableVariables.sortOrder || 'descend') as SortOrder)
            ://:undefined
            null,
        width: columnWidths[`${String(key)}::${typeof rest.title === 'string' ? rest.title : index}`] ?? width,
        ellipsis: !(rest.title === 'Actions' || rest.fixed) && { showTitle: false },
        ...rest,
        onHeaderCell: (column: TableColumnType<RecordType>): ResizeableTitleProps => {
          column.ellipsis = false;
          return {
            width:
              (typeof column.width === 'string'
                ? Number(column.width.match(/^\d+/)?.[0])
                : column.width) || 100,
            onResize: (e, { size }) => {
              const resizeKey = `${String(key)}::${typeof rest.title === 'string' ? rest.title : index}`;
              setColumnWidths((previous) => ({ ...previous, [resizeKey]: size.width }));
            },
          };
        },
      }),
    );
  }, [columnWidths, propColumns, tableVariables.sortBy, tableVariables.sortOrder]);

  const [api, { loading: apiLoading }] = useApi({ errorNotification: true });
  const loading = propLoading || apiLoading;

  const fetchData = useCallback(
    (apiParameters?: Record<string, string>) => {
      if (isFetchingRef.current) {
        return;
      }
      isFetchingRef.current = true;

      const currentParams = hasFilters ? apiParams : apiParamsPropsRef.current;
      const currentHeaders = hasFilters ? apiHeaders : apiHeadersPropsRef.current;

      api({
        input: apiInputProps,
        params: {
          ...currentParams,
          ...tableVariables,
          sortOrder: tableVariables.sortOrder === 'ascend' ? 'ASC' : 'DESC',
          ...(apiParameters && apiParameters.fromDate && apiParameters.toDate
            ? { fromDate: apiParameters.fromDate, toDate: apiParameters.toDate }
            : {}),
        },
        headers: currentHeaders,
      })
        .then((res) => {
          onDataRef.current?.(res);
          const { data: apiData } = res;
          const items = get(apiData, dataKey) || [];
          const data = transformData?.(items) ?? items;
          const totalKey = dataKey.split('.').slice(0, -1).concat(['totalCount']).join('.');
          const total = get(apiData, totalKey) || data.length;
          setTableData({ total, data });
        })
        .catch((err) => onError?.(err))
        .finally(() => {
          isFetchingRef.current = false;
        });
    },
    [api, apiInputProps, apiParams, tableVariables, apiHeaders, dataKey, transformData, onError, hasFilters],
  );

  const handlePageChange = useCallback(
    (page?: number) => {
      if (pageSizeChange.current) {
        pageSizeChange.current = false;
        return;
      }
      if (page === 1) page = undefined;
      if (useQuery) setQuery({ page }, 'pushIn');
      setTableVariables((prevVars) => ({ ...prevVars, page }));
    },
    [useQuery, setQuery],
  );

  const handlePageSizeChange = useCallback(
    (page: number, pageSize: number) => {
      pageSizeChange.current = true;
      if (useQuery) setQuery({ page: undefined, pageSize }, 'pushIn');
      setTableVariables((prevVars) => ({ ...prevVars, page: undefined, limit: pageSize }));
    },
    [useQuery, setQuery],
  );

  const handleChange = useCallback<NonNullable<TableProps<RecordType>['onChange']>>(
    (pagination, filters, sorter, extra) => {
      if (extra?.action !== 'sort') {
        return;
      }
      const [sort] = castArray(sorter);
      const field = castArray(sort.field).join('.') || undefined;
      if (tableVariables.sortBy === field && tableVariables.sortOrder === sort.order) {
        return;
      }
      const newData = {
        sortBy: sort.order === undefined ? undefined : field,
        sortOrder: sort.order,
      };
      if (useQuery) setQuery(newData, 'pushIn');
      setTableVariables((prevVars) => ({ ...prevVars, ...newData }));
    },
    [tableVariables.sortBy, tableVariables.sortOrder, useQuery, setQuery],
  );

  const handleSubmit = useCallback(
    (params: any, headers: any) => {
      const hasDateRange = params?.fromDate && params?.toDate;
      const newParams = hasDateRange
        ? {
          ...params,
          toDate: dayjs(params.toDate).format('YYYY-MM-DDTHH:mm:ss'),
          fromDate: dayjs(params.fromDate).format('YYYY-MM-DDTHH:mm:ss'),
        }
        : params;
      onFiltersSubmit?.(newParams, headers);
      setTableVariables((prevVars) => ({
        ...prevVars,
        page: prevVars.page && prevVars.page > 1 ? undefined : prevVars.page,
        sortBy: query.sortBy ?? undefined,
        sortOrder: (query.sortOrder ?? undefined) as SortOrder | undefined,
      }));
      setApiParams(merge({}, apiParamsProps, newParams));
      setApiHeaders(merge({}, apiHeadersProps, headers));
    },
    [apiHeadersProps, apiParamsProps, onFiltersSubmit, query.sortBy, query.sortOrder],
  );

  useEffect(() => {
    if (initialMount.current) {
      if (!hasFilters && autoSubmit) {
        fetchData();
      }
      initialMount.current = false;
    }
    else if (hasFilters) {
      // When filters exist and apiParams/apiHeaders change, fetch new data
      fetchData();
    }
  }, [
    apiParams,
    apiHeaders,
    autoSubmit,
    hasFilters,
    fetchData
  ]);

  const apiParamsPropsKey = useMemo(() => JSON.stringify(apiParamsProps), [apiParamsProps]);
  const skipApiParamsSync = useRef(true);
  useEffect(() => {
    if (skipApiParamsSync.current) {
      skipApiParamsSync.current = false;
      return;
    }
    if (!hasFilters) return;
    setApiParams((prev) => ({ ...prev, ...apiParamsPropsRef.current }));
  }, [apiParamsPropsKey, hasFilters]);

  useImperativeHandle(ref, () => ({
    reload: () => fetchData(apiParamsProps),
    getCurrentParams: () => {
      const normalizedSortOrder =
        tableVariables.sortOrder === 'ascend'
          ? 'ASC'
          : tableVariables.sortOrder === 'descend'
            ? 'DESC'
            : undefined;

      return {
        ...(hasFilters ? apiParams : apiParamsPropsRef.current),
        ...tableVariables,
        sortOrder: normalizedSortOrder,
      };
    },
  }));

  useEffect(() => {
    if (!initialMount.current) {
      setTableVariables((prev) => ({
        ...prev,
        page: query.page,
        limit: query.pageSize ?? prev.limit,
        sortBy: query.sortBy ?? undefined,
        sortOrder: (query.sortOrder ?? undefined) as SortOrder | undefined,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.page, query.pageSize, query.sortBy, query.sortOrder]);


  useEffect(() => {
    if (!initialMount.current && !hasFilters) {
      fetchData();
    }
  }, [
    fetchData,
    hasFilters,
    apiParamsPropsKey,
    tableVariables.page,
    tableVariables.limit,
    tableVariables.sortBy,
    tableVariables.sortOrder
  ]);

  return (
    <>
      {filtersMap && showFilters ? (
        <Filters
          filtersMap={filtersMap}
          loading={loading}
          onValidSubmit={handleSubmit}
          onReset={onFiltersReset}
          autoSubmit={autoSubmit}
          {...filtersProps}
        />
      ) : null}

      {render ? (
        render({
          data: tableData.data,
          loading,
          pagination: {
            defaultPageSize: tableVariables.limit,
            pageSizeOptions: ['25', '50', '75', '100'],
            showQuickJumper: Boolean(tableData.total),
            showSizeChanger: true,
            onChange: handlePageChange,
            onShowSizeChange: handlePageSizeChange,
            current: tableVariables.page || 1,
            total: tableData.total,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} Items`,
          },
        })
      ) : (
        <Table
          className="data-table"
          components={components}
          dataSource={tableData.data}
          rowKey={rowKey}
          columns={transformedColumns}
          loading={loading}
          tableLayout="fixed"
          bordered
          size="small"
          scroll={{
            x: 'max-content',
            scrollToFirstRowOnChange: true,
          }}
          onChange={handleChange}
          pagination={{
            defaultPageSize: tableVariables.limit,
            pageSizeOptions: ['25', '50', '75', '100'],
            showQuickJumper: Boolean(tableData.total),
            showSizeChanger: true,
            onChange: handlePageChange,
            onShowSizeChange: handlePageSizeChange,
            current: tableVariables.page || 1,
            total: tableData.total,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} Items`,
          }}
          {...tableProps}
        />
      )}
    </>
  );
}

export default forwardRef(DataTable);
